import { useSearchParams } from "react-router-dom";
import type { AppDataState } from "../api/useAppData";
import HeadToHeadCard from "../components/HeadToHeadCard";
import OwnerTag from "../components/OwnerTag";
import { KNOCKOUT_STAGE_ORDER } from "../../shared/stages";

function formatGroup(group: string) {
  if (group.startsWith("GROUP_")) return `Group ${group.slice(6)}`;
  return group;
}

function formatGD(n: number) {
  return n > 0 ? `+${n}` : String(n);
}

export default function StandingsPage({ state }: { state: AppDataState }) {
  const { data } = state;
  const [params, setParams] = useSearchParams();
  const stage = params.get("stage") === "knockout" ? "knockout" : "groups";

  if (!data) return <p>Loading…</p>;

  return (
    <>
      <h1>Standings</h1>
      <div className="segmented" role="tablist" aria-label="Standings stage">
        <button
          role="tab"
          aria-selected={stage === "groups"}
          className={stage === "groups" ? "active" : ""}
          onClick={() => setParams({ stage: "groups" })}
        >
          Groups
        </button>
        <button
          role="tab"
          aria-selected={stage === "knockout"}
          className={stage === "knockout" ? "active" : ""}
          onClick={() => setParams({ stage: "knockout" })}
        >
          Knockout
        </button>
      </div>

      {stage === "groups" ? (
        <GroupsView data={data} />
      ) : (
        <KnockoutView data={data} />
      )}
    </>
  );
}

function GroupsView({ data }: { data: NonNullable<AppDataState["data"]> }) {
  const groups = Object.keys(data.groupStandings).sort();
  if (groups.length === 0) {
    return <div className="card empty-card">No group data yet.</div>;
  }
  return (
    <div
      className="section"
      style={{ display: "grid", gap: 14, gridTemplateColumns: "1fr" }}
    >
      {groups.map((group) => {
        const rows = data.groupStandings[group];
        return (
          <div key={group} className="card group-card">
            <h3>{formatGroup(group)}</h3>
            <table className="standings-table">
              <thead>
                <tr>
                  <th>Pos</th>
                  <th className="team-cell">Team</th>
                  <th className="team-cell">Owner</th>
                  <th>P</th>
                  <th>W</th>
                  <th>D</th>
                  <th>L</th>
                  <th>GF</th>
                  <th>GA</th>
                  <th>GD</th>
                  <th>Pts</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.team}>
                    <td>{i + 1}</td>
                    <td className="team-cell">{row.team}</td>
                    <td className="team-cell">
                      {row.owner ? <OwnerTag owner={row.owner} /> : "—"}
                    </td>
                    <td>{row.played}</td>
                    <td>{row.won}</td>
                    <td>{row.drawn}</td>
                    <td>{row.lost}</td>
                    <td>{row.goalsFor}</td>
                    <td>{row.goalsAgainst}</td>
                    <td>{formatGD(row.goalDifference)}</td>
                    <td>
                      <strong>{row.points}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

function KnockoutView({ data }: { data: NonNullable<AppDataState["data"]> }) {
  const stages = KNOCKOUT_STAGE_ORDER.filter(
    (s) => (data.knockoutFixtures[s] ?? []).length > 0
  );

  if (stages.length === 0) {
    return <div className="card empty-card">No knockout fixtures yet.</div>;
  }

  return (
    <div className="section">
      {stages.map((stage) => (
        <section key={stage} className="knockout-stage">
          <h3>{stage}</h3>
          <div className="h2h-grid">
            {data.knockoutFixtures[stage].map((f) => (
              <HeadToHeadCard key={f.id} fixture={f} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
